import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Reusable SEO component — wraps react-helmet-async.
 *
 * Props:
 *   title         — page title (required). Will be wrapped with " | SEOintent" suffix unless titleExact=true.
 *   description   — meta description (required, ~150-160 chars)
 *   canonical     — canonical URL (e.g. "https://seointent.com/blog/slug"). Defaults to window.location.
 *   image         — OG/Twitter image URL. Defaults to https://seointent.com/logo-red.png
 *   type          — og:type. Defaults to "website". Use "article" for blog posts.
 *   schemas       — array of JSON-LD objects to inject as <script type="application/ld+json">
 *   noindex       — if true, adds robots=noindex,nofollow
 *   titleExact    — if true, uses title as-is without the " | SEOintent" suffix
 *   keywords      — optional comma-separated keywords
 *   author        — optional author name (for articles)
 *   publishedAt   — ISO date (for articles)
 *   modifiedAt    — ISO date (for articles)
 */
const SITE_URL = "https://seointent.com";
const SITE_NAME = "SEOintent";
const DEFAULT_IMAGE = "https://seointent.com/logo-red.png";
const TWITTER_HANDLE = "@seointent";

// SEOintent has no verified reviews yet, so rating/review markup is a fabricated
// claim (Google spam policy → manual action risk). Older pSEO/blog docs in Mongo
// still carry a hardcoded aggregateRating, so strip it at render time.
const UNVERIFIED_SCHEMA_KEYS = new Set(["aggregateRating", "review", "reviews"]);
function stripUnverifiedClaims(node) {
  if (Array.isArray(node)) return node.map(stripUnverifiedClaims);
  if (node && typeof node === "object") {
    return Object.fromEntries(
      Object.entries(node)
        .filter(([k]) => !UNVERIFIED_SCHEMA_KEYS.has(k))
        .map(([k, v]) => [k, stripUnverifiedClaims(v)]),
    );
  }
  return node;
}
function serializeSchema(schema) {
  if (typeof schema !== "string") return JSON.stringify(stripUnverifiedClaims(schema));
  try {
    return JSON.stringify(stripUnverifiedClaims(JSON.parse(schema)));
  } catch (_) {
    return schema;
  }
}

export default function SEO({
  title,
  description,
  canonical,
  image = DEFAULT_IMAGE,
  type = "website",
  schemas = [],
  noindex = false,
  titleExact = false,
  keywords,
  author,
  publishedAt,
  modifiedAt,
}) {
  const fullTitle = titleExact ? title : `${title} | ${SITE_NAME}`;
  // Canonical must ALWAYS resolve to the apex (https://seointent.com) — never
  // www, never api., never with query strings or hashes. Without this, GSC
  // sees `https://www.seointent.com/foo` and `https://seointent.com/foo` as
  // two separate pages and splits the ranking signals (observed in our own
  // GSC data, 2026-05-11).
  let url = canonical;
  if (!url) {
    if (typeof window !== "undefined") {
      url = SITE_URL + window.location.pathname;
    } else {
      url = SITE_URL;
    }
  }

  return (
    <Helmet>
      {/* Core meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {publishedAt && <meta property="article:published_time" content={publishedAt} />}
      {modifiedAt && <meta property="article:modified_time" content={modifiedAt} />}
      {author && type === "article" && <meta property="article:author" content={author} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Schemas */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {serializeSchema(schema)}
        </script>
      ))}
    </Helmet>
  );
}

/**
 * Common reusable schemas — import and spread as needed.
 */
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "SEOintent",
  legalName: "SEOintent",
  url: SITE_URL,
  logo: `${SITE_URL}/logo-red.png`,
  foundingDate: "2025",
  email: "hello@seointent.com",
  description:
    "SEOintent is an AI-powered SEO automation platform built for agencies and small businesses. Auto-cluster keywords, generate articles with Claude, optimize to RankMath 90+, and publish to WordPress or Shopify in minutes.",
  sameAs: [
    "https://socialorange.in",
  ],
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "SEOintent",
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/blog?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "SEOintent",
  operatingSystem: "Web",
  applicationCategory: "BusinessApplication",
  url: SITE_URL,
  description:
    "AI-powered SEO automation platform — auto-cluster keywords, generate SEO articles with Claude, optimize to RankMath 90+, publish to WordPress and Shopify.",
  offers: [
    {
      "@type": "Offer",
      name: "Growth Plan",
      price: "99",
      priceCurrency: "USD",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "99",
        priceCurrency: "USD",
        billingIncrement: "monthly",
      },
    },
    {
      "@type": "Offer",
      name: "Scale Plan",
      price: "199",
      priceCurrency: "USD",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "199",
        priceCurrency: "USD",
        billingIncrement: "monthly",
      },
    },
  ],
};

export function breadcrumbSchema(items) {
  // items = [{name, url}, ...]
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function blogPostingSchema({ title, description, image, slug, author, publishedAt, modifiedAt }) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    image: image || DEFAULT_IMAGE,
    author: {
      "@type": "Person",
      name: author || "SEOintent",
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/logo-red.png`,
      },
    },
    datePublished: publishedAt,
    dateModified: modifiedAt || publishedAt,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}/blog/${slug}`,
    },
  };
}

export function faqSchema(faqs) {
  // faqs = [{question, answer}, ...]
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.answer,
      },
    })),
  };
}
